import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import PinterestIcon from '@mui/icons-material/Pinterest';
import ShareIcon from '@mui/icons-material/Share';
import XIcon from '@mui/icons-material/X';

// WeChat icon (custom SVG)
const WeChatIcon: React.FC<{ fontSize?: 'small' | 'medium' | 'large' }> = ({ fontSize }) => (
  <SvgIcon fontSize={fontSize} viewBox='0 0 24 24'>
    <path d='M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178A1.17 1.17 0 014.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 01-1.162 1.178 1.17 1.17 0 01-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 01.598.082l1.584.926a.272.272 0 00.14.045c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 01-.023-.156.49.49 0 01.201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.032zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 01-.969.983.976.976 0 01-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 01-.969.983.976.976 0 01-.969-.983c0-.542.434-.982.969-.982z' />
  </SvgIcon>
);

interface ShareButtonProps {
  title: string;
  description?: string;
  imageUrl?: string;
  url?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({
  title,
  description = '',
  imageUrl,
  url = window.location.href,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [wechatDialogOpen, setWechatDialogOpen] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showSnackbar = (message: string) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showSnackbar('Link copied to clipboard');
    } catch {
      showSnackbar('Failed to copy link');
    }
    handleClose();
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url,
        });
      } catch (err) {
        // User cancelled or share failed - ignore
        if ((err as Error).name !== 'AbortError') {
          showSnackbar('Failed to share');
        }
      }
    }
    handleClose();
  };

  const handleTwitterShare = () => {
    const text = encodeURIComponent(`${title}\n${description}`);
    const shareUrl = encodeURIComponent(url);
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${shareUrl}`,
      '_blank',
      'noopener,noreferrer',
    );
    handleClose();
  };

  const handleFacebookShare = () => {
    const shareUrl = encodeURIComponent(url);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      '_blank',
      'noopener,noreferrer',
    );
    handleClose();
  };

  const handlePinterestShare = () => {
    const shareUrl = encodeURIComponent(url);
    const descriptionEncoded = encodeURIComponent(title);
    const media = imageUrl ? encodeURIComponent(imageUrl) : '';
    window.open(
      `https://pinterest.com/pin/create/button/?url=${shareUrl}&description=${descriptionEncoded}${
        media ? `&media=${media}` : ''
      }`,
      '_blank',
      'noopener,noreferrer',
    );
    handleClose();
  };

  const handleWeChatShare = () => {
    setWechatDialogOpen(true);
    handleClose();
  };

  const supportsNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  // Generate QR code URL using a free API
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
    url,
  )}`;

  return (
    <>
      <Tooltip title='Share recipe'>
        <IconButton
          onClick={handleClick}
          aria-label='Share recipe'
          aria-controls={open ? 'share-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
        >
          <ShareIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id='share-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon>
            <ContentCopyIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Copy link</ListItemText>
        </MenuItem>

        {supportsNativeShare && (
          <MenuItem onClick={handleNativeShare}>
            <ListItemIcon>
              <LinkIcon fontSize='small' />
            </ListItemIcon>
            <ListItemText>Share via...</ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleTwitterShare}>
          <ListItemIcon>
            <XIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Share on X</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleFacebookShare}>
          <ListItemIcon>
            <FacebookIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Share on Facebook</ListItemText>
        </MenuItem>

        <MenuItem onClick={handlePinterestShare}>
          <ListItemIcon>
            <PinterestIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Share on Pinterest</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleWeChatShare}>
          <ListItemIcon>
            <WeChatIcon fontSize='small' />
          </ListItemIcon>
          <ListItemText>Share on WeChat</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={wechatDialogOpen} onClose={() => setWechatDialogOpen(false)} maxWidth='xs'>
        <DialogTitle
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          Share on WeChat
          <IconButton size='small' onClick={() => setWechatDialogOpen(false)} aria-label='Close'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Scan this QR code with WeChat to share
            </Typography>
            <Box
              component='img'
              src={qrCodeUrl}
              alt='QR Code for WeChat sharing'
              sx={{ width: 200, height: 200, border: '1px solid', borderColor: 'divider' }}
            />
            <Typography variant='caption' display='block' color='text.secondary' sx={{ mt: 2 }}>
              {title}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default ShareButton;
